import React, { useCallback, useEffect } from "react";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import baseUrl from "../../../../../api/baseUrl";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";

interface AddCategoryProps {
  closeModal: () => void;
  id?: string;
}

interface FormData {
  categoryName: string;
}

const AddCategory: React.FC<AddCategoryProps> = ({ closeModal, id }) => {
  const methods = useForm<FormData>();

  const getCategoryData = useCallback(
    (categoryId: string) => {
      baseUrl
        .get("client/inventory/category/findCategoryById", {
          headers: { categoryid: categoryId },
        })
        .then((response) => {
          methods.reset(response.data.data);
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [methods]
  );

  useEffect(() => {
    if (id) {
      getCategoryData(id);
    }
  }, [id, getCategoryData]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      const apiCall = id
        ? baseUrl.patch("client/inventory/category/updateCategoryById", data, {
            headers: { categoryid: id },
          })
        : baseUrl.post("client/inventory/category/createCategory", data);

      apiCall
        .then(() => closeModal())
        .catch((error) => console.error(error?.response));
    },
    [closeModal, id]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[8px] shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-100 rounded-md flex items-center justify-center">
              <CategoryIcon className="text-emerald-600" sx={{ fontSize: 14 }} />
            </div>
            <h2 className="text-xs font-bold text-slate-800">
              {id ? "Edit Category" : "Add Category"}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="p-3">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                {...methods.register("categoryName", { required: true })}
                type="text"
                placeholder="Enter category name"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs"
              />
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2.5 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors font-medium text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
              >
                {id ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddCategory;
